import { useState } from "react";
import axios from "axios";
import { Upload, CheckCircle2 } from "lucide-react";
import { PageHero, useTitle } from "../components/PageHero";
import { Reveal, Tag } from "../components/Reveal";
import { REQUIREMENT_TYPES, CITIES, waLink } from "../data/site";
import { INDUSTRIES } from "../data/industries";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full border border-seam bg-obsidian px-4 py-3.5 text-sm text-frost placeholder:text-cadmium/50 focus:border-spark focus:outline-none";
const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-widest text-cadmium";

const FileField = ({ label, name, onChange, file, testid, multiple = false, accept }) => (
  <div>
    <span className={labelCls}>{label}</span>
    <label data-testid={testid}
      className="flex cursor-pointer items-center gap-3 border border-dashed border-seam bg-obsidian px-4 py-3.5 text-sm text-cadmium transition-colors hover:border-spark">
      <Upload size={16} className="shrink-0 text-spark" />
      <span className="truncate">
        {file ? (multiple ? `${file.length} file(s) selected` : file.name) : "Click to upload"}
      </span>
      <input type="file" name={name} multiple={multiple} accept={accept} className="hidden"
        onChange={(e) => onChange(multiple ? Array.from(e.target.files) : e.target.files[0])} />
    </label>
  </div>
);

export default function RFQ() {
  useTitle("Request a Quote — RFQ | SABAKT Saudi Arabia");
  const [form, setForm] = useState({
    name: "", company: "", job_title: "", email: "", mobile: "", city: "Riyadh",
    industry: "", requirement_type: "", description: "", equipment: "", quantity: "", delivery_date: "",
  });
  const [drawing, setDrawing] = useState(null);
  const [boq, setBoq] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (drawing) fd.append("drawing", drawing);
      if (boq) fd.append("boq", boq);
      photos.forEach((p) => fd.append("photos", p));
      const res = await axios.post(`${API}/rfq`, fd);
      setResult(res.data);
      window.scrollTo(0, 0);
    } catch {
      setError("Submission failed. Please try again, or reach us on WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  if (result) {
    return (
      <main>
        <section className="flex min-h-[80vh] items-center justify-center px-4 pt-[72px]" data-testid="rfq-success">
          <Reveal className="w-full max-w-xl border border-spark/50 bg-iron p-10 text-center">
            <CheckCircle2 size={48} className="mx-auto text-spark" />
            <h1 className="mt-6 font-display text-4xl font-black uppercase tracking-tight text-frost">RFQ Submitted</h1>
            <p className="mt-3 font-mono text-sm tracking-widest text-spark" data-testid="rfq-ref-code">REF: {result.ref}</p>
            <p className="mt-4 text-base leading-relaxed text-cadmium">{result.message}</p>
            <a href={waLink(`Hello SABAKT, I just submitted RFQ ${result.ref}.`)} target="_blank" rel="noopener noreferrer"
              data-testid="rfq-success-whatsapp"
              className="mt-8 inline-block border border-seam px-7 py-4 font-display text-sm font-bold uppercase tracking-wider text-frost hover:border-spark hover:text-spark">
              Follow Up on WhatsApp
            </a>
          </Reveal>
        </section>
      </main>
    );
  }

  return (
    <main>
      <PageHero eyebrow="Request for Quotation · KSA" testid="rfq-hero"
        title="Request a Technical Quote." accent="Quote."
        text="The more complete the RFQ, the sharper the quote. Attach drawings, photos or a BOQ — our engineering team reviews every submission." />

      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-8">
          <Reveal>
            <form onSubmit={submit} className="border border-seam bg-iron p-8 lg:p-12" data-testid="rfq-form">
              <Tag>Your Details</Tag>
              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <div><span className={labelCls}>Name *</span>
                  <input required value={form.name} onChange={set("name")} className={inputCls} data-testid="rfq-name-input" /></div>
                <div><span className={labelCls}>Company</span>
                  <input value={form.company} onChange={set("company")} className={inputCls} data-testid="rfq-company-input" /></div>
                <div><span className={labelCls}>Job Title</span>
                  <input value={form.job_title} onChange={set("job_title")} className={inputCls} data-testid="rfq-jobtitle-input" /></div>
                <div><span className={labelCls}>Email *</span>
                  <input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="rfq-email-input" /></div>
                <div><span className={labelCls}>Saudi Mobile Number *</span>
                  <input required placeholder="+966 5X XXX XXXX" value={form.mobile} onChange={set("mobile")} className={inputCls} data-testid="rfq-mobile-input" /></div>
                <div><span className={labelCls}>City</span>
                  <select value={form.city} onChange={set("city")} className={inputCls} data-testid="rfq-city-select">
                    {CITIES.map((c) => <option key={c}>{c}</option>)}
                  </select></div>
              </div>

              <div className="mt-10"><Tag>The Requirement</Tag></div>
              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <div><span className={labelCls}>Industry</span>
                  <select value={form.industry} onChange={set("industry")} className={inputCls} data-testid="rfq-industry-select">
                    <option value="">Select industry</option>
                    {INDUSTRIES.map((i) => <option key={i.slug}>{i.name}</option>)}
                    <option>Other</option>
                  </select></div>
                <div><span className={labelCls}>Requirement Type</span>
                  <select value={form.requirement_type} onChange={set("requirement_type")} className={inputCls} data-testid="rfq-type-select">
                    <option value="">Select type</option>
                    {REQUIREMENT_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select></div>
                <div className="sm:col-span-2"><span className={labelCls}>Project Description *</span>
                  <textarea required rows={4} value={form.description} onChange={set("description")}
                    placeholder="Describe the requirement, operating conditions, material handled, wear pattern or failure..."
                    className={inputCls} data-testid="rfq-description-input" /></div>
                <div><span className={labelCls}>Equipment / Component</span>
                  <input value={form.equipment} onChange={set("equipment")} placeholder="e.g. CAT 330 excavator bucket" className={inputCls} data-testid="rfq-equipment-input" /></div>
                <div><span className={labelCls}>Quantity</span>
                  <input value={form.quantity} onChange={set("quantity")} className={inputCls} data-testid="rfq-quantity-input" /></div>
                <div><span className={labelCls}>Required Delivery Date</span>
                  <input type="date" value={form.delivery_date} onChange={set("delivery_date")} className={inputCls} data-testid="rfq-date-input" /></div>
              </div>

              <div className="mt-10"><Tag>Attachments</Tag></div>
              <div className="mt-5 grid gap-5 sm:grid-cols-3">
                <FileField label="Upload Drawing" name="drawing" file={drawing} onChange={setDrawing} testid="rfq-drawing-upload" accept=".pdf,.dwg,.dxf,.png,.jpg,.jpeg" />
                <FileField label="Upload Photos" name="photos" file={photos.length ? photos : null} onChange={setPhotos} testid="rfq-photos-upload" multiple accept="image/*" />
                <FileField label="Upload RFQ / BOQ" name="boq" file={boq} onChange={setBoq} testid="rfq-boq-upload" accept=".pdf,.xls,.xlsx,.csv,.doc,.docx" />
              </div>

              {error && <p className="mt-6 border border-red-500/50 bg-red-500/10 px-4 py-3 text-sm text-red-400" data-testid="rfq-error">{error}</p>}

              <button type="submit" disabled={sending} data-testid="rfq-submit-btn"
                className="ember-glow mt-10 w-full bg-gold px-7 py-5 font-display text-lg font-bold uppercase tracking-wider text-white transition-colors hover:bg-spark disabled:opacity-50">
                {sending ? "Submitting RFQ..." : "Submit RFQ"}
              </button>
              <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-widest text-cadmium/60">
                Reviewed by SABAKT engineering · Riyadh, Saudi Arabia
              </p>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
